import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FiArrowLeft, FiAward, FiTruck, FiHeart, FiShield } from 'react-icons/fi';


const About = () => {
  return (
    <>
      <Helmet>
        <title>À Propos d'ORIMA - Notre Histoire & Valeurs | Boutique Luxe Maroc</title>
        <meta 
          name="description" 
          content="Découvrez l'histoire d'ORIMA, votre boutique de luxe au Maroc depuis 2024. Plus de 15 000 clients satisfaits. Accessoires premium, livraison gratuite, satisfaction garantie." 
        />
        <meta 
          name="keywords" 
          content="à propos ORIMA, boutique luxe Maroc, histoire ORIMA, accessoires premium Casablanca, e-commerce luxe Maroc, qualité premium, livraison gratuite Maroc" 
        />
        <link rel="canonical" href="https://yoursite.com/about" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://yoursite.com/about" />
        <meta property="og:title" content="À Propos d'ORIMA - Votre Boutique Luxe au Maroc" />
        <meta property="og:description" content="Fondée en 2024, ORIMA est votre destination premium pour les accessoires de mode au Maroc. Plus de 15 000 clients satisfaits. Qualité, confiance, service exceptionnel." />
        <meta property="og:image" content="https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1400&q=80" />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://yoursite.com/about" />
        <meta property="twitter:title" content="À Propos d'ORIMA - Votre Boutique Luxe au Maroc" />
        <meta property="twitter:description" content="Fondée en 2024, ORIMA est votre destination premium pour les accessoires de mode au Maroc. Plus de 15 000 clients satisfaits." />
        <meta property="twitter:image" content="https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1400&q=80" />

        {/* Structured Data for Organization */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "ORIMA",
            "url": "https://yoursite.com",
            "logo": "https://yoursite.com/logo.png",
            "description": "ORIMA est une boutique en ligne premium spécialisée dans les accessoires de mode de luxe au Maroc. Fondée en 2024.",
            "address": {
              "@type": "PostalAddress",
              "addressCountry": "MA",
              "addressLocality": "Casablanca"
            },
            "foundingDate": "2024",
            "slogan": "Votre destination premium pour les accessoires de mode",
            "sameAs": [
              "https://www.facebook.com/orima",
              "https://www.instagram.com/orima",
              "https://www.tiktok.com/@orima"
            ]
          })}
        </script>

        {/* Structured Data for Breadcrumb */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Accueil",
                "item": "https://yoursite.com/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "À Propos",
                "item": "https://yoursite.com/about"
              }
            ]
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Header */}
        <div className="bg-gray-900 text-white py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-sm text-gray-300 hover:text-white mb-6 transition-colors"
            >
              <FiArrowLeft />
              Retour à l'accueil
            </Link>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold">
              À Propos d'ORIMA
            </h1>
            <p className="text-gray-300 mt-4 text-lg">
              Votre destination premium pour les accessoires de mode
            </p>
          </div>
        </div>

        {/* Hero Image Section */}
        <div className="relative h-[300px] sm:h-[400px] overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1400&q=80"
            alt="ORIMA Store - Boutique de luxe au Maroc"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/30"></div>
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          
          {/* Notre Histoire */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Notre Histoire</h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                Fondée en 2024, <strong>ORIMA</strong> est née d'une passion pour les accessoires 
                de mode de qualité et d'un désir de rendre le luxe accessible à tous les Marocains. 
                Notre nom, ORIMA, évoque l'excellence et la distinction que nous nous efforçons 
                d'apporter à chaque produit que nous proposons.
              </p>
              <p>
                Spécialisés dans la vente de sacs, montres, portefeuilles et accessoires premium, 
                nous sélectionnons méticuleusement chaque article pour garantir à nos clients 
                une expérience shopping exceptionnelle. Notre engagement est simple : offrir 
                des produits de qualité supérieure à des prix justes.
              </p>
              <p>
                Depuis notre lancement, nous avons servi plus de 15 000 clients satisfaits à 
                travers tout le Maroc, de Casablanca à Marrakech, en passant par Rabat, Fès et Tanger.
              </p>
            </div>
          </section>

          {/* Notre Mission */}
          <section className="mb-16 bg-gray-50 p-8 rounded-2xl">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Notre Mission</h2>
            <p className="text-gray-700 text-lg leading-relaxed">
              Chez ORIMA, notre mission est de démocratiser l'accès aux accessoires de mode 
              premium tout en maintenant les plus hauts standards de qualité et de service client. 
              Nous croyons que chaque personne mérite de se sentir élégante et confiante, 
              et nous nous engageons à rendre cela possible.
            </p>
          </section>

          {/* Nos Valeurs */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Nos Valeurs</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="text-center p-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
                  <FiAward className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Qualité Premium</h3>
                <p className="text-gray-600">
                  Chaque produit est sélectionné avec soin pour garantir une qualité exceptionnelle 
                  et une durabilité à long terme.
                </p>
              </div>

              <div className="text-center p-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                  <FiTruck className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Livraison Rapide</h3>
                <p className="text-gray-600">
                  Nous nous engageons à livrer vos commandes dans les meilleurs délais, 
                  partout au Maroc, gratuitement.
                </p>
              </div>

              <div className="text-center p-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                  <FiHeart className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Satisfaction Client</h3>
                <p className="text-gray-600">
                  Votre satisfaction est notre priorité. Notre équipe est disponible 24/7 
                  pour répondre à vos besoins.
                </p>
              </div>

              <div className="text-center p-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                  <FiShield className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Confiance & Sécurité</h3>
                <p className="text-gray-600">
                  Paiement à la livraison et politique de retour flexible pour votre 
                  tranquillité d'esprit.
                </p>
              </div>
            </div>
          </section>

          {/* Notre Engagement */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Notre Engagement</h2>
            <div className="bg-gray-900 text-white p-8 rounded-2xl">
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <span className="text-2xl">✓</span>
                  <span>Produits authentiques et de qualité supérieure</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-2xl">✓</span>
                  <span>Livraison gratuite partout au Maroc</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-2xl">✓</span>
                  <span>Paiement sécurisé à la livraison</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-2xl">✓</span>
                  <span>Service client réactif et disponible</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-2xl">✓</span>
                  <span>Droit de retour sous 7 jours</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-2xl">✓</span>
                  <span>Prix compétitifs et promotions régulières</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Pourquoi Choisir ORIMA */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Pourquoi Choisir ORIMA ?</h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                <strong>Une sélection rigoureuse :</strong> Nous visitons et testons personnellement 
                chaque fournisseur pour garantir que seuls les meilleurs produits arrivent 
                jusqu'à vous.
              </p>
              <p>
                <strong>Des prix justes :</strong> En travaillant directement avec nos partenaires, 
                nous éliminons les intermédiaires et vous proposons des prix compétitifs sans 
                compromettre la qualité.
              </p>
              <p>
                <strong>Une expérience client exceptionnelle :</strong> De la navigation sur 
                notre site à la réception de votre commande, nous mettons tout en œuvre pour 
                vous offrir une expérience fluide et agréable.
              </p>
            </div>
          </section>

          {/* Contact CTA */}
          <section className="text-center py-12 bg-gray-50 rounded-2xl">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Contactez-nous</h2>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              Une question ? Une suggestion ? Notre équipe est à votre écoute.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="https://wa.me/212665078092"
                className="bg-gray-900 text-white px-8 py-3 rounded-full font-semibold hover:bg-gray-800 transition-all"
              >
                Nous Contacter
              </Link>
              <Link
                to="/shop"
                className="border-2 border-gray-900 text-gray-900 px-8 py-3 rounded-full font-semibold hover:bg-gray-900 hover:text-white transition-all"
              >
                Découvrir nos Produits
              </Link>
            </div>
          </section>

          {/* Back button */}
          <div className="text-center mt-12 pt-8 border-t border-gray-200">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 font-semibold"
            >
              <FiArrowLeft />
              Retour à l'accueil
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default About;
